import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Grid,
  ThemeProvider,
} from "@mui/material";
import {
  CredentialDefenseFunnel,
  SecondFactorCard,
  MostTargetedAccounts,
} from "@/components/CredentialHealth";
import { useCredentialHealth } from "@/hooks/useDashboard.ts";
import { colors, eventsLightTheme } from "@/theme/eventsTokens.ts";
import { formatShare } from "@/utils/formatShare.ts";
import { formatSnapshotFreshness } from "@/utils/snapshotFreshness.ts";
import { attackTotals, noAttackTrafficMessage } from "@/utils/attackTraffic.ts";
import type { CredentialHealthData } from "@/types";

export default function CredentialHealthPage() {
  const { data, isLoading, error } = useCredentialHealth();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load credential health data.
      </Alert>
    );
  }

  if (!data) return null;

  return (
    <ThemeProvider theme={eventsLightTheme}>
      <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: colors.textPrimary }}>
          Credential Health
        </Typography>
        <Subtitle data={data} />
        <Grid container spacing={2}>
          <Grid size={12}>
            <CredentialDefenseFunnel funnel={data.funnel} windowDays={data.window_days} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <SecondFactorCard breakdown={data.second_factor} windowDays={data.window_days} />
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <MostTargetedAccounts data={data} />
          </Grid>
        </Grid>
      </Container>
    </ThemeProvider>
  );
}

function Subtitle({ data }: { readonly data: CredentialHealthData }) {
  const freshness = formatSnapshotFreshness(data.as_of);
  return (
    <Box sx={{ mt: 0.5, mb: 3 }}>
      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
        {subtitleText(data)}
      </Typography>
      <Typography variant="caption" sx={{ color: colors.textMuted, display: "block", mt: 0.5 }}>
        Last {data.window_days} days{freshness ? ` · snapshot as of ${freshness}` : ""}
      </Typography>
    </Box>
  );
}

function subtitleText(data: CredentialHealthData): string {
  const { funnel, window_days } = data;
  const { attackTotal, reachedCreds } = attackTotals(funnel);
  if (attackTotal === 0) return noAttackTrafficMessage(window_days);
  return (
    `Shield blocked ${funnel.blocked.toLocaleString()} of ${attackTotal.toLocaleString()} attack attempts ` +
    `(${formatShare(funnel.blocked, attackTotal)}) from hostile sources before they reached your credentials. ` +
    `${reachedCreds.toLocaleString()} got through.`
  );
}
